import { useEffect, useState } from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { AlertCircle, ArrowRight, Eye, EyeOff, Lock, ShieldCheck, User } from 'lucide-react'
import { ApiError } from '@/lib/api-client'
import { env } from '@/lib/env'
import { Button } from '@/components/ui/Button'
import { Field } from '@/components/ui/Field'
import { Input } from '@/components/ui/Input'
import { NotAnAdminError } from './auth-context'
import { useAuth } from './useAuth'
import styles from './LoginPage.module.css'

const schema = z.object({
  identifier: z
    .string()
    .trim()
    .min(1, 'Enter the email address or phone number on the account'),
  password: z.string().min(1, 'Enter your password'),
})

type LoginValues = z.infer<typeof schema>

interface LocationState {
  from?: string
}

const SIGN_OUT_MESSAGES: Record<string, string> = {
  expired: 'Your session expired. Please sign in again.',
  forbidden: 'Your access was revoked. Please sign in again.',
}

export function LoginPage() {
  const { isAuthenticated, signIn, signOutReason, clearSignOutReason } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [formError, setFormError] = useState<string | null>(null)
  const [showPassword, setShowPassword] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginValues>({
    resolver: zodResolver(schema),
    defaultValues: { identifier: '', password: '' },
  })

  const from = (location.state as LocationState | null)?.from

  useEffect(() => {
    document.title = `Sign in · ${env.appName} Operations`
  }, [])

  if (isAuthenticated) {
    return <Navigate to={from && from !== '/login' ? from : '/'} replace />
  }

  const onSubmit = async (values: LoginValues) => {
    setFormError(null)
    clearSignOutReason()

    try {
      await signIn({ identifier: values.identifier.trim(), password: values.password })
      navigate(from && from !== '/login' ? from : '/', { replace: true })
    } catch (error) {
      // Each failure needs a different response from the person reading it:
      // wrong password, suspended account, not an admin, or API unreachable.
      if (error instanceof NotAnAdminError) {
        setFormError(error.message)
        return
      }
      if (error instanceof ApiError) {
        if (error.isUnauthorized) {
          setFormError('That email/phone and password combination was not recognised.')
          return
        }
        setFormError(error.message)
        return
      }
      setFormError('Sign-in failed. Please try again.')
    }
  }

  const notice = signOutReason ? SIGN_OUT_MESSAGES[signOutReason] : undefined

  return (
    <div className={styles.page}>
      {/* Brand panel. Hidden on small screens where the form is what matters. */}
      <aside className={styles.brandPanel} aria-hidden="true">
        <div className={styles.brandGrid} />
        <div className={styles.brandContent}>
          <img src="/brand/mark.svg" alt="" className={styles.brandMark} />
          <p className={styles.brandEyebrow}>Drive For Me</p>
          <h1 className={styles.brandTitle}>
            Operations
            <span>Console</span>
          </h1>
          <p className={styles.brandCopy}>
            Approve trip requests, dispatch drivers and keep every journey moving — from one place.
          </p>
          <ul className={styles.brandStats}>
            <li>
              <span>Dispatch</span>Assign and reassign drivers in seconds
            </li>
            <li>
              <span>Lifecycle</span>Start, extend and close trips safely
            </li>
            <li>
              <span>Growth</span>Run promotions and watch them land
            </li>
          </ul>
        </div>
      </aside>

      <main className={styles.formPanel}>
        <div className={styles.formInner}>
          <img src="/brand/mark.svg" alt="Drive For Me" className={styles.formMark} />

          <header className={styles.formHeader}>
            <h2 className={styles.formTitle}>Sign in</h2>
            <p className={styles.formSubtitle}>Administrator access to the Drive For Me operations console.</p>
          </header>

          {notice ? (
            <p className={styles.notice} role="status">
              <ShieldCheck aria-hidden="true" />
              {notice}
            </p>
          ) : null}

          {formError ? (
            <p className={styles.error} role="alert">
              <AlertCircle aria-hidden="true" />
              {formError}
            </p>
          ) : null}

          <form className={styles.form} onSubmit={(event) => void handleSubmit(onSubmit)(event)} noValidate>
            <Field label="Email or phone number" error={errors.identifier?.message} required>
              {({ id, describedBy, invalid }) => (
                <Input
                  id={id}
                  aria-describedby={describedBy}
                  invalid={invalid}
                  autoComplete="username"
                  autoFocus
                  placeholder="admin@driveforme.com"
                  iconLeft={<User aria-hidden="true" />}
                  {...register('identifier')}
                />
              )}
            </Field>

            <Field label="Password" error={errors.password?.message} required>
              {({ id, describedBy, invalid }) => (
                <Input
                  id={id}
                  aria-describedby={describedBy}
                  invalid={invalid}
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  placeholder="••••••••"
                  iconLeft={<Lock aria-hidden="true" />}
                  addonRight={
                    <button
                      type="button"
                      className={styles.reveal}
                      onClick={() => setShowPassword((current) => !current)}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? <EyeOff aria-hidden="true" /> : <Eye aria-hidden="true" />}
                    </button>
                  }
                  {...register('password')}
                />
              )}
            </Field>

            <Button
              type="submit"
              variant="primary"
              size="lg"
              fullWidth
              loading={isSubmitting}
              iconRight={isSubmitting ? undefined : <ArrowRight aria-hidden="true" />}
            >
              {isSubmitting ? 'Signing in…' : 'Sign in'}
            </Button>
          </form>

          <p className={styles.footnote}>
            Sessions end when this browser tab is closed. Access is limited to accounts with the administrator role.
          </p>
        </div>
      </main>
    </div>
  )
}
